import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { theme } from '../../../domain/theme';
import { imageResizerService } from '../../../domain/services/imageResizerService';
import { AmbientPlayer } from '../../AmbientPlayer';
import { CallToActionButton } from '../CallToActionButton';
import { ParagraphWrapper } from '../ParagraphWrapper';
import { Image } from '../../Image';
import { stripTags, getCase } from '../../../domain/utils/stringUtils';

const colorConfig = {
  white: { background: 'white', color: 'black' },
  gray: { background: '#f7f7f7', color: 'black' },
  black: { background: 'black', color: 'white' }
};

const gradientColors = {
  white: '#ffffff',
  gray: '#f7f7f7',
  black: '#000000'
};

const InlineTextInner = ({ data }) => {
  const rawBackgroundColor = data.backgroundColor || 'white';
  const title = data.inlineTextTitle?.value;
  const titleCase = title ? getCase(stripTags(title)) : null;
  const hideYellowBar = !!data.hideYellowBar;
  const text = data.inlineTextParagraph?.value;
  let textAlign = data.textAlign || 'center';
  const { textPanel } = data;
  if (textPanel && textAlign === 'center') {
    textAlign = 'left';
  }
  const buttonStyle = data.buttonStyle || 'primary';
  const hasVideo = !!data.bgVideo?.entity?.guid;
  const hasImage = !hasVideo && !!data.bgImage?.entity?.mediaImage?.url;

  const backgroundImageEntity = imageResizerService.overrideImageFocalPoint(
    data.bgImage
  );
  const backgroundImage = backgroundImageEntity?.mediaImage?.url;

  const backgroundImageUrls = backgroundImage
    ? imageResizerService.getStyleUrlsByBreakpoint(backgroundImage, {
        position: backgroundImageEntity?.focalPoint
      })
    : null;

  const colors = colorConfig[rawBackgroundColor];
  const backgroundColor = colors.background;
  const fontColor = colors.color;

  const opacity = data.mediaTint ? (100 - data.mediaTint) / 100 : 1;
  let tintColor = backgroundColor;
  if (data.theming === 'light') {
    tintColor = '#fff';
  }
  if (data.theming === 'dark') {
    tintColor = '#000';
  }
  const backgroundImageStyles = hasImage
    ? css`
        background: url(${backgroundImageUrls.mobile}) no-repeat
          ${imageResizerService.getBackgroundFocalPoint(
            backgroundImageEntity?.focalPoint
          )};
        ${theme.mq.tablet} {
          background-image: url(${backgroundImageUrls.tablet});
        }
        ${theme.mq.small_desktop} {
          background-image: url(${backgroundImageUrls.small_desktop});
        }
        ${theme.mq.large_desktop} {
          background-image: url(${backgroundImageUrls.large_desktop});
        }
      `
    : null;

  let textPanelStyles;
  if (textPanel) {
    if (textPanel === 'gradient') {
      textPanelStyles = css`
        background: ${backgroundColor};
        ${theme.mq.small_desktop} {
          background: linear-gradient(
            ${['left', 'start'].includes(textAlign) ? 270 : 90}deg,
            ${gradientColors[rawBackgroundColor]}00 0%,
            ${gradientColors[rawBackgroundColor]} 100%
          );
        }
      `;
    } else {
      textPanelStyles = css`
        background: ${backgroundColor};
      `;
    }
  }

  const styles = {
    inlineContainer: css`
      overflow: hidden;
      background-color: ${backgroundColor};
      clear: both;
      width: 100%;
      position: relative;
      p a {
        border-bottom: 3px solid #fc0;
        color: ${fontColor};
        &:hover {
          background: #fc0;
          color: #000;
        }
      }
      strong {
        font-weight: ${theme.font.weight('xl')};
      }
    `,
    inlineWrapper: css`
      color: ${fontColor};
      background-color: ${tintColor};
      position: relative;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: grid;
      place-items: ${textAlign};
      text-align: ${textAlign};
      &:before {
        display: ${hasVideo ? 'none' : 'block'};
        ${backgroundImageStyles}
        background-size: cover;
        content: '';
        position: absolute;
        top: 0;
        right: ${textPanel === 'solid' && textAlign === 'end' ? '50%' : 0};
        bottom: 0;
        left: ${textPanel === 'solid' && textAlign === 'start' ? '50%' : 0};
        opacity: ${opacity};
      }
    `,
    inlineContentBox: css`
      ${textPanelStyles};
      position: relative;
      ${theme.mq.small_desktop} {
        width: ${textPanel ? '50% ' : null};
      }
    `,
    inlineContentBoxWrapper: css`
      margin: 0 auto;
      padding: ${textPanel ? '20px' : '40px 20px'};
      padding-top: ${textPanel === 'gradient' ? '0' : null};
      ${theme.mq.tablet} {
        padding: ${textPanel ? '50px' : '56px 50px'};
        padding-top: ${textPanel === 'gradient' ? '0' : null};
      }
      ${theme.mq.small_desktop} {
        width: ${textPanel ? '80%' : null};
        max-width: ${textPanel ? '1080px' : null};
        padding-top: 56px;
      }
      ${theme.mq.large_desktop} {
        padding-top: 64px;
      }
    `,
    inlineTitle: css`
      ${theme.font.family('boldWeb')};
      font-size: 32px;
      font-weight: 600;
      line-height: 1.42;
      letter-spacing: ${titleCase === 'uppercase' ? '3.6px' : null};
      h2 {
        font-size: 32px;
        font-weight: 600;
        line-height: 1.42;
        letter-spacing: ${titleCase === 'uppercase' ? '3.6px' : null};
        margin: 0;
      }
      position: relative;
      margin: 0 auto 20px auto;
      max-width: 1080px;
      ${theme.mq.small_desktop} {
        font-size: 44px;
        line-height: 1.3;
        h2 {
          font-size: 44px;
          line-height: 1.3;
        }
      }
    `,
    inlineText: css`
      ${theme.font.family('regular')};
      font-size: 18px;
      font-weight: 400;
      line-height: 1.5;
      letter-spacing: initial;
      margin: 0 auto 20px;
      max-width: 1080px;
      p {
        font-size: 18px;
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: initial;
      }
    `,
    yellowLine: css`
      display: inline-block;
      background-color: ${title && text ? '#fc0' : 'transparent'};
      height: 3px;
      margin: 0 0 13px 0;
      width: 40px;
      z-index: 1;
    `,
    video: css`
      position: ${textPanel ? 'relative' : 'absolute'};
      aspect-ratio: ${textPanel ? '16 / 9' : null};
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
      padding-top: 0;
      opacity: ${textPanel ? 1 : opacity};
      ${theme.mq.small_desktop} {
        position: absolute;
        aspect-ratio: unset;
        opacity: ${opacity};
      }
    `,
    videoInner: css`
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      height: auto;
      width: 100%;
      position: absolute;
    `,
    textPanelMedia: css`
      ${theme.mq.small_desktop} {
        display: none;
      }
    `,
    imageContainer: css`
      position: relative;
      &:before {
        content: '';
        display: ${textPanel === 'gradient' ? 'block' : 'none'};
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        background: linear-gradient(
          180deg,
          ${gradientColors[rawBackgroundColor]}00 60%,
          ${gradientColors[rawBackgroundColor]} 100%
        );
      }
    `
  };

  return (
    <div css={styles.inlineContainer}>
      {hasVideo && (
        <div css={styles.video}>
          <div css={styles.videoInner}>
            <AmbientPlayer size="cover" videoEntity={data.bgVideo.entity} />
          </div>
        </div>
      )}

      {textPanel && hasImage && (
        <div css={styles.textPanelMedia}>
          {hasImage && (
            <div css={styles.imageContainer}>
              <Image entity={data.bgImage.entity} />
            </div>
          )}
        </div>
      )}

      <div css={styles.inlineWrapper}>
        <div css={styles.inlineContentBox}>
          <div css={styles.inlineContentBoxWrapper}>
            {title && (
              <div
                css={styles.inlineTitle}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: title }}
              />
            )}
            {!hideYellowBar && <div css={styles.yellowLine} />}
            {text && (
              <div
                css={styles.inlineText}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: text }}
              />
            )}

            {!!data.link?.url?.path && (
              <CallToActionButton
                url={data.link.url.path}
                text={data.link.title}
                align={textAlign}
                type={buttonStyle}
                canvasColor={rawBackgroundColor}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

InlineTextInner.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired
};

export const InlineText = ParagraphWrapper(InlineTextInner);
